import React, { useContext, useReducer, useState } from "react";
import { Spinner } from "../../../useOss";
import Modal from "../../../useOss/Modal";
import { PopupContentContext } from "../../../context/PopupContentContext";
import { InputErrorMessage } from "../../../dashboard/saas/Settings/InputErrorMessage";
import updatePassword from "../../../api/user/auth/password/post";
import { validatePasswordInput } from "../../../dashboard/saas/Settings/utils";
import { UserInfoResponse } from "../../../api/user/info";
import { sendUserSettingsAnalytics } from "../../../utils";

type FormState = {
    values: {
        confirmPassword: string;
        currentPassword: string;
        newPassword: string;
    };
    errors: {
        confirmPassword: string | null;
        currentPassword: string | null;
        newPassword: string | null;
    };
    isVisible: {
        confirmPassword: boolean;
        currentPassword: boolean;
        newPassword: boolean;
    };
};
type FormAction =
    | { type: "update_confirmPassword"; payload: string }
    | { type: "update_newPassword"; payload: string }
    | { type: "update_currentPassword"; payload: string }
    | {
          type: "update_field_error";
          payload: {
              field: "confirmPassword" | "newPassword" | "currentPassword";
              error: string;
          };
      }
    | {
          type: "update_field_visibility";
          payload: {
              field: "confirmPassword" | "newPassword" | "currentPassword";
              isVisible: boolean;
          };
      }
    | { type: "reset_form" };

const INITIAL_FORM_STATE: FormState = {
    values: {
        confirmPassword: "",
        currentPassword: "",
        newPassword: ""
    },
    errors: {
        confirmPassword: null,
        currentPassword: null,
        newPassword: null
    },
    isVisible: {
        confirmPassword: false,
        currentPassword: false,
        newPassword: false
    }
};

function PasswordEyeIcon({ opened, onClick }: { opened: boolean; onClick: () => void }) {
    return (
        <img
            src={!opened ? "/static/assets/dashboard/eye-open.png" : "/static/assets/dashboard/eye-close.png"}
            className="password-eye-icon"
            onClick={onClick}
        />
    );
}

function formReducer(state: FormState, action: FormAction) {
    switch (action.type) {
        case "update_confirmPassword": {
            return {
                ...state,
                values: {
                    ...state.values,
                    confirmPassword: action.payload
                },
                errors: {
                    ...state.errors,
                    confirmPassword: state.values.newPassword !== action.payload ? "Passwords do not match" : null
                }
            };
        }
        case "update_currentPassword": {
            return {
                ...state,
                values: {
                    ...state.values,
                    currentPassword: action.payload
                },
                errors: {
                    ...state.errors,
                    currentPassword: null
                }
            };
        }
        case "update_newPassword": {
            let confirmPasswordError: string | null = null;
            let newPasswordError = !action.payload ? "Password cannot be empty" : null;

            if (state.values.confirmPassword && action.payload !== state.values.confirmPassword) {
                confirmPasswordError = "Passwords do not match";
            }
            const response = validatePasswordInput(action.payload);
            if (!response.success) {
                newPasswordError = response.error;
            }

            return {
                ...state,
                values: {
                    ...state.values,
                    newPassword: action.payload
                },
                errors: {
                    ...state.errors,
                    newPassword: newPasswordError,
                    confirmPassword: confirmPasswordError
                }
            };
        }
        case "update_field_error": {
            return {
                ...state,
                errors: {
                    ...state.errors,
                    [action.payload.field]: action.payload.error
                }
            };
        }
        case "reset_form": {
            return INITIAL_FORM_STATE;
        }
        case "update_field_visibility": {
            return {
                ...state,
                isVisible: {
                    ...state.isVisible,
                    [action.payload.field]: action.payload.isVisible
                }
            };
        }
        default: {
            return state;
        }
    }
}
export function ChangePasswordModal({
    isModalOpen,
    closeModal,
    user
}: {
    isModalOpen: boolean;
    closeModal: () => void;
    user: UserInfoResponse;
}) {
    const [state, setState] = useState<"IDLE" | "SAVING">("IDLE");
    const [formState, setFormState] = useReducer(formReducer, INITIAL_FORM_STATE);

    const { showToast } = useContext(PopupContentContext);
    const isSaving = state === "SAVING";
    const formHasErrors = Object.values(formState.errors).some(val => !!val);
    const formHasEmptyValue = Object.values(formState.values).some(val => !val);
    const disableSaving = isSaving || formHasEmptyValue || formHasErrors;

    function close() {
        setFormState({ type: "reset_form" });
        closeModal();
    }

    async function handlePasswordUpdate(e: React.MouseEvent<HTMLButtonElement, MouseEvent>) {
        e.preventDefault();
        if (disableSaving) return;
        try {
            setState("SAVING");
            const response = await updatePassword(formState.values.currentPassword, formState.values.newPassword);
            if (response.success) {
                showToast({
                    status: "success",
                    icon: `/static/assets/dashboard/check-rounded-green.png`,
                    children: <>Password Updated</>
                });
                sendUserSettingsAnalytics("password_update", {
                    userId: user.userId,
                    email: user.email
                });
                close();
            } else {
                switch (response.field) {
                    case "oldPassword": {
                        setFormState({
                            type: "update_field_error",
                            payload: {
                                field: "currentPassword",
                                error: response.issue
                            }
                        });
                        break;
                    }
                    case "newPassword": {
                        setFormState({
                            type: "update_field_error",
                            payload: {
                                field: "newPassword",
                                error: response.issue
                            }
                        });
                        break;
                    }
                }
            }
        } catch (e) {
            showToast({
                status: "error",
                icon: `/static/assets/dashboard/red-issue.png`,
                children: <>Password Update Failed</>
            });
        }
        setState("IDLE");
    }

    function renderSaveButton() {
        switch (state) {
            case "IDLE":
                return (
                    <button
                        className={`modal-button yellow-button ${isSaving && "loading"}`}
                        style={{
                            width: "65px",
                            height: "37px"
                        }}
                        disabled={disableSaving}
                        onClick={handlePasswordUpdate}
                    >
                        Save
                    </button>
                );
            case "SAVING":
                return (
                    <button
                        className="modal-button yellow-button loading"
                        disabled
                        style={{
                            color: "#FFFFFF",
                            backgroundColor: "#FF9933",
                            border: "1px solid #FF8A15",
                            width: "65px",
                            height: "37px"
                        }}
                    >
                        <Spinner isInline />
                    </button>
                );
        }
    }

    return (
        <Modal show={isModalOpen} modalClosed={() => close()} className={`${!isModalOpen && "hide"}`}>
            <section className="modal-header-section centered">
                <h4>Change password</h4>

                <svg
                    width="22"
                    height="22"
                    viewBox="0 0 22 22"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    onClick={() => close()}
                >
                    <path
                        d="M11 22C17.0751 22 22 17.0751 22 11C22 4.92487 17.0751 0 11 0C4.92487 0 0 4.92487 0 11C0 17.0751 4.92487 22 11 22Z"
                        fill="#E8E8E8"
                    />
                    <path
                        d="M7.2481 14.89C7.40424 15.0431 7.61474 15.1279 7.83337 15.1258C8.052 15.1238 8.26088 15.035 8.4141 14.879L11.0041 12.239L13.5951 14.879C13.6699 14.9606 13.7604 15.0264 13.8611 15.0723C13.9618 15.1182 14.0708 15.1433 14.1814 15.1463C14.2921 15.1492 14.4022 15.1298 14.5052 15.0893C14.6082 15.0488 14.7021 14.9879 14.7811 14.9104C14.8601 14.8329 14.9227 14.7402 14.9651 14.638C15.0076 14.5357 15.029 14.426 15.0282 14.3153C15.0274 14.2046 15.0043 14.0952 14.9603 13.9936C14.9163 13.8921 14.8523 13.8004 14.7721 13.724L12.1601 11.063L14.7721 8.40103C14.9167 8.24332 14.9943 8.03562 14.9887 7.82175C14.983 7.60787 14.8945 7.40456 14.7418 7.25471C14.5891 7.10486 14.3841 7.0202 14.1702 7.01858C13.9562 7.01696 13.7501 7.09851 13.5951 7.24603L11.0041 9.88603L8.4141 7.24603C8.3393 7.16443 8.24883 7.09872 8.14809 7.05281C8.04736 7.0069 7.93841 6.98173 7.82775 6.9788C7.71709 6.97588 7.60698 6.99526 7.50396 7.03578C7.40094 7.0763 7.30712 7.13714 7.22811 7.21467C7.1491 7.29221 7.0865 7.38485 7.04405 7.48709C7.00159 7.58932 6.98014 7.69907 6.98098 7.80977C6.98182 7.92046 7.00493 8.02986 7.04893 8.13144C7.09293 8.23302 7.15692 8.3247 7.2371 8.40103L9.8491 11.063L7.2371 13.724C7.08405 13.8802 6.99924 14.0907 7.00131 14.3093C7.00337 14.5279 7.09214 14.7368 7.2481 14.89Z"
                        fill="#6E6A65"
                    />
                </svg>
            </section>
            <hr />
            <form className="auth-container">
                <div className="auth-item">
                    <span className="asterisk">Current Password</span>
                    <div className="password-input-wrapper">
                        <input
                            type={formState.isVisible.currentPassword ? "text" : "password"}
                            value={formState.values.currentPassword}
                            onChange={e => {
                                setFormState({ type: "update_currentPassword", payload: e.target.value });
                            }}
                            autoComplete="current-password"
                        />
                        <PasswordEyeIcon
                            opened={formState.isVisible.currentPassword}
                            onClick={() => {
                                setFormState({
                                    type: "update_field_visibility",
                                    payload: {
                                        field: "currentPassword",
                                        isVisible: !formState.isVisible.currentPassword
                                    }
                                });
                            }}
                        />
                    </div>
                    {<InputErrorMessage errorMessage={formState.errors.currentPassword} />}
                </div>
                <div className="auth-item error">
                    <span className="asterisk">New Password</span>
                    <div className="password-input-wrapper">
                        <input
                            type={formState.isVisible.newPassword ? "text" : "password"}
                            className={`${!!formState.errors.newPassword && "error"}`}
                            value={formState.values.newPassword}
                            onChange={e => {
                                setFormState({ type: "update_newPassword", payload: e.target.value });
                            }}
                            autoComplete="new-password"
                        />
                        <PasswordEyeIcon
                            opened={formState.isVisible.newPassword}
                            onClick={() => {
                                setFormState({
                                    type: "update_field_visibility",
                                    payload: {
                                        field: "newPassword",
                                        isVisible: !formState.isVisible.newPassword
                                    }
                                });
                            }}
                        />
                    </div>
                    {<InputErrorMessage errorMessage={formState.errors.newPassword} />}
                </div>
                <div className="auth-item error">
                    <span className="asterisk">Confirm Password</span>
                    <div className="password-input-wrapper">
                        <input
                            type={formState.isVisible.confirmPassword ? 'text' : "password"}
                            className={`${!!formState.errors.confirmPassword && "error"}`}
                            value={formState.values.confirmPassword}
                            onChange={e => {
                                setFormState({ type: "update_confirmPassword", payload: e.target.value });
                            }}
                            autoComplete="confirm-password"
                        />
                        <PasswordEyeIcon
                            opened={formState.isVisible.confirmPassword}
                            onClick={() => {
                                setFormState({
                                    type: "update_field_visibility",
                                    payload: {
                                        field: "confirmPassword",
                                        isVisible: !formState.isVisible.confirmPassword
                                    }
                                });
                            }}
                        />
                    </div>
                    {<InputErrorMessage errorMessage={formState.errors.confirmPassword} />}
                </div>
            </form>
            <div
                style={{
                    display: "flex",
                    justifyContent: "flex-end"
                }}
            >
                <button
                    className="modal-button white-button"
                    style={{
                        marginRight: "24px"
                    }}
                    onClick={() => close()}
                >
                    Cancel
                </button>
                {renderSaveButton()}
            </div>
        </Modal>
    );
}
